import { HighlightText } from "@/components/highlight-text"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchItem } from "@/components/ui/search"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { FreeTextMedicationCreation } from "@/modules/patients/detail/medication/components/free-text-medication-creation"
import { SearchError } from "@/modules/patients/detail/medication/components/search-error"
import { PrescriptionDataType } from "@/modules/patients/detail/prescription/de/schema/prescription-schema"
import { useDeMedicationSearchQuery$data } from "@/modules/patients/services/de/__generated__/useDeMedicationSearchQuery.graphql"
import { ArrElement } from "@/types/ArrElement"

export type Medication = ArrElement<useDeMedicationSearchQuery$data["de_preparations"]["result"]>
export type Substance = ArrElement<useDeMedicationSearchQuery$data["de_substances"]["result"]>
export type MedicationSearchResultsType = {
  preparation?: Medication
  substance?: Substance
  freeTextMedicationName?: string
}

export type PrescriptionMedicationSearchResults = MedicationSearchResultsType & PrescriptionDataType

interface SearchResultsProps {
  isVisible: boolean
  searchValue: string
  isError: boolean
  onMedicationSelection: (selected: Medication) => void
  onSubstanceSelection: (selected: Substance) => void
  onFreeTextMedicationSelection: () => void
  results: useDeMedicationSearchQuery$data | null | undefined
  ref?: React.Ref<HTMLDivElement>
}

export const SearchResults = ({
  results,
  isVisible,
  isError,
  searchValue,
  onMedicationSelection,
  onSubstanceSelection,
  onFreeTextMedicationSelection,
  ref,
  ...props
}: SearchResultsProps) => {
  const de_preparations = results?.de_preparations
  const de_substances = results?.de_substances
  const hasResults =
    (de_preparations?.result && de_preparations.result.length > 0) ||
    (de_substances?.result && de_substances.result.length > 0)

  return (
    <SearchContent ref={ref} className={cn("animate-in fade-in-20", isVisible ? "show" : "hidden")} {...props}>
      {isError && <SearchError />}
      {!hasResults && !isError && (
        <FreeTextMedicationCreation searchTerm={searchValue} onClick={onFreeTextMedicationSelection} />
      )}
      {hasResults && !isError && (
        <ScrollArea data-testid="medication-search-results">
          <div className="py-2" role="group">
            {de_substances?.result?.map((substance) => {
              return (
                <SearchItem
                  key={substance.nameHtml}
                  className="overflow-hidden rounded-md py-1.5"
                  onSelect={() => {
                    onSubstanceSelection(substance)
                  }}
                  value={substance.substanceId.toString()}
                  data-testid="medication-search-item">
                  <div className="grid">
                    {/* oxlint-disable-next-line typescript/no-deprecated */}
                    <HighlightText
                      className="text-on-surface truncate text-sm"
                      title={substance.nameHtml}
                      text={substance.nameHtml}
                      searchValue={searchValue.trim()}
                      extendedMarkClasses="font-normal"
                    />
                  </div>
                </SearchItem>
              )
            })}

            {de_preparations?.result?.map((preparation) => {
              const formattedName = joinStrings([preparation.name, ...preparation.composition])
              const pharmaceuticalName = preparation.pharmaceuticalForm?.name?.trim()

              return (
                <SearchItem
                  key={preparation.productId}
                  className="rounded-md py-1"
                  value={preparation.productId.toString()}
                  onSelect={() => onMedicationSelection(preparation)}
                  data-testid="medication-search-item">
                  <div className="grid">
                    {/* oxlint-disable-next-line typescript/no-deprecated */}
                    <HighlightText
                      className="text-on-surface truncate text-sm"
                      title={formattedName}
                      text={formattedName}
                      searchValue={searchValue.trim()}
                      extendedMarkClasses="font-normal"
                    />
                    {pharmaceuticalName && (
                      <span className="text-on-surface-subdued text-xs">{pharmaceuticalName}</span>
                    )}
                    {preparation.pharmaceuticalCentralNumberList?.map((pharma) => {
                      const formattedPreparationText = `${pharma.unit} (${pharma.quantity}) ${pharma.price}`
                      return (
                        <span key={pharma.pharmaceuticalCentralNumber} className="text-on-surface-subdued text-xs">
                          {formattedPreparationText}
                        </span>
                      )
                    })}
                  </div>
                </SearchItem>
              )
            })}
          </div>
        </ScrollArea>
      )}
    </SearchContent>
  )
}
